(() => {
  if (window.ProductFomoCounter) return;

  const instances = new WeakMap();
  const pollInterval = 30000;
  const tickDuration = 20000;

  class ProductFomoCounter {
    constructor(container) {
      this.container = container;
      this.selectedVariantId = container.dataset.variantId;
      this.pollTimer = null;
      this.animationFrame = null;
      this.activeController = null;

      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleOnline = this.handleOnline.bind(this);

      document.addEventListener('variantPriceChanged', this.handleVariantChange);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('online', this.handleOnline);

      if (this.isInventoryEligible() && this.getStock() > 0) {
        this.scheduleNextPoll();
      }
    }

    getStock() {
      const stock = Number.parseInt(this.container.dataset.stock || '0', 10);
      return Number.isFinite(stock) ? stock : 0;
    }

    isInventoryEligible() {
      return this.container.dataset.inventoryEligible === 'true';
    }

    getSelectedVariantId() {
      const variantInput = this.container.closest('form')?.querySelector('[name="id"]');
      return variantInput?.value || this.selectedVariantId;
    }

    getPollingInterval() {
      // A small jitter prevents visitors who land together from polling in lockstep.
      return Math.round(pollInterval * (0.9 + Math.random() * 0.2));
    }

    scheduleNextPoll(delay = this.getPollingInterval()) {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = window.setTimeout(() => this.fetchInventory(), delay);
    }

    updateLiveStatus(stock, isVisible) {
      const liveStatus = this.container.querySelector('.fomo-live-status');
      if (liveStatus) liveStatus.textContent = isVisible ? `Only ${stock} left in stock.` : '';
    }

    updateVisual(stock, isVisible) {
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;

      const counter = this.container.querySelector('.product-fomo-stock-counter');
      const number = counter?.querySelector('.fomo-number');
      if (number) number.textContent = stock;
      if (counter) counter.hidden = !isVisible;

      this.updateLiveStatus(stock, isVisible);
    }

    animateValue(element, start, end) {
      window.cancelAnimationFrame(this.animationFrame);

      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        element.textContent = end;
        this.updateLiveStatus(end, true);
        return;
      }

      let startTime;

      const tick = (timestamp) => {
        startTime ??= timestamp;
        const progress = Math.min((timestamp - startTime) / tickDuration, 1);
        const nextValue = Math.max(end, Math.floor(start - (start - end) * progress));
        element.textContent = nextValue;

        if (progress < 1) {
          this.animationFrame = window.requestAnimationFrame(tick);
        } else {
          this.animationFrame = null;
          this.updateLiveStatus(end, true);
        }
      };

      this.animationFrame = window.requestAnimationFrame(tick);
    }

    async fetchInventory(force = false) {
      window.clearTimeout(this.pollTimer);

      if (document.hidden || !navigator.onLine) return;
      if (!force && (!this.isInventoryEligible() || this.getStock() <= 0)) return;

      this.activeController?.abort();
      const controller = new AbortController();
      this.activeController = controller;

      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('section_id', 'product-fomo-inventory');
      const selectedVariantId = this.getSelectedVariantId();
      if (selectedVariantId) url.searchParams.set('variant', selectedVariantId);

      // Share a short-lived URL between visitors while avoiding a stale browser response.
      url.searchParams.set('_fomo', Math.floor(Date.now() / 15000));

      try {
        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(`Inventory request failed with HTTP ${response.status}`);
        }

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const fetchedContainer = documentFragment.querySelector('[data-fomo-inventory]');

        if (!fetchedContainer) {
          throw new Error('Inventory counter was not found in the section response');
        }

        const fetchedStock = Number.parseInt(fetchedContainer.dataset.stock || '0', 10);
        if (!Number.isFinite(fetchedStock)) throw new Error('Inventory response contained an invalid stock value');

        const fetchedVariantId = fetchedContainer.dataset.variantId || '';
        const fetchedInventoryEligible = fetchedContainer.dataset.inventoryEligible === 'true';
        const fetchedCounterVisible = fetchedInventoryEligible && fetchedStock > 0;
        const currentVariantId = this.container.dataset.variantId || '';
        const currentStock = this.getStock();
        const currentCounter = this.container.querySelector('.product-fomo-stock-counter');
        const currentNumber = this.container.querySelector('.fomo-number');
        const currentCounterVisible = currentCounter ? !currentCounter.hidden : false;
        const variantChanged = currentVariantId !== fetchedVariantId;
        const visibilityChanged = currentCounterVisible !== fetchedCounterVisible;

        if (variantChanged || visibilityChanged) {
          this.updateVisual(fetchedStock, fetchedCounterVisible);
        } else if (currentNumber && currentCounterVisible && fetchedStock < currentStock) {
          this.animateValue(currentNumber, currentStock, fetchedStock);
        } else if (fetchedStock !== currentStock) {
          this.updateVisual(fetchedStock, fetchedCounterVisible);
        }

        this.container.dataset.stock = fetchedStock;
        this.container.dataset.variantId = fetchedVariantId;
        this.container.dataset.inventoryEligible = fetchedInventoryEligible ? 'true' : 'false';
        this.selectedVariantId = fetchedVariantId;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('FOMO inventory sync error:', error);
        }
      } finally {
        if (this.activeController !== controller) return;
        this.activeController = null;

        if (document.hidden || !navigator.onLine) return;

        if (this.isInventoryEligible() && this.getStock() > 0) {
          this.scheduleNextPoll();
        }
      }
    }

    handleVariantChange(event) {
      const variantId = event.detail?.id;
      if (variantId) this.selectedVariantId = String(variantId);
      this.fetchInventory(true);
    }

    handleVisibilityChange() {
      if (document.hidden) {
        window.clearTimeout(this.pollTimer);
        this.activeController?.abort();
      } else {
        this.fetchInventory(true);
      }
    }

    handleOnline() {
      this.fetchInventory(true);
    }

    destroy() {
      window.clearTimeout(this.pollTimer);
      window.cancelAnimationFrame(this.animationFrame);
      this.activeController?.abort();
      document.removeEventListener('variantPriceChanged', this.handleVariantChange);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('online', this.handleOnline);
      instances.delete(this.container);
    }
  }

  function initialize(root = document) {
    root.querySelectorAll('[data-fomo-counter]').forEach((container) => {
      if (!instances.has(container)) instances.set(container, new ProductFomoCounter(container));
    });
  }

  window.ProductFomoCounter = { initialize };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-fomo-counter]').forEach((container) => instances.get(container)?.destroy());
  });
})();
